import { useMemo } from 'react';
import { useAuthStore } from '@features/authentication/store/authStore';
import { useAnnouncementStore } from '@features/announcements/store';
import { useDocumentCaptureStore } from '@features/document-capture';
import {
  filterMenuByPermission,
  isSubGroup,
  menuItems,
  type MenuChild,
  type MenuEntry,
} from '../lib/menu';

/**
 * The nav tree as THIS user may see it: permission-filtered, with live badge counts injected.
 *
 * One hook rather than a predicate copied per surface. The rail (M1), the module panel (M2) and its
 * search (M5) all consume it, so a permission rule is written once and every shell surface agrees
 * about what exists. Previously this lived as a `hasAny` closure inside a `useMemo` in `Sidebar.tsx`,
 * reachable by nothing else.
 *
 * The FILTERING itself is `filterMenuByPermission` — a pure function in `lib/menu.ts`, tested
 * without React (M9). What stays here is the part that cannot be pure: badges come from stores.
 *
 * ⚠️ React Compiler: `hasPermission` is subscribed as a value and the badge counts are subscribed
 * directly, so the memo's dependencies are all things the compiler can see. Do not switch to
 * calling a store getter inside the memo body (86d3d1z40).
 */
export function useVisibleMenu(): MenuEntry[] {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const activeAnnouncementCount = useAnnouncementStore((s) => s.activeAnnouncements.length);
  // Document Capture review badge (86d3rrfbf). Value selector, never a store getter called in
  // render — see the React-Compiler note in CLAUDE.md.
  const needsReviewCount = useDocumentCaptureStore((s) => s.needsReviewCount);

  return useMemo(() => {
    const visible = filterMenuByPermission(menuItems, hasPermission);

    // Badges are injected AFTER filtering, so a count can never resurrect an entry the user may not
    // see, and the filter stays a pure function of permissions.
    return visible.map((entry): MenuEntry => {
      if (entry.type === 'item') {
        return entry.href === '/' ? { ...entry, badge: activeAnnouncementCount } : entry;
      }
      return {
        ...entry,
        children: entry.children.map((child): MenuChild => {
          if (isSubGroup(child)) return child;
          return child.href === '/documents' ? { ...child, badge: needsReviewCount } : child;
        }),
      };
    });
  }, [hasPermission, activeAnnouncementCount, needsReviewCount]);
}
